import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import FiltersService from "../services/api/filters/filters.service";
import {
  COLLABORATORS_PAGE_SIZE,
  HISTORY_PROJECT_COMPANY_PAGE_SIZE,
  HISTORY_PROJECT_ROLES_PAGE_SIZE,
  HISTORY_PROJECT_SECTORS_PAGE_SIZE,
  RefreshReactQuery,
} from "../constants/AppConstants";
import CollaboratorsHistoryProjectsService from "../services/api/collaborators/CollaboratorsHistoryProjects.service";
import { FilterProps } from "../types/AppTypes";
import { useNotification } from "./notification.hook";
import {
  DATA_ADDED_SUCCESS_LABEL,
  DATA_DELETED_SUCCESS_LABEL,
  DATA_UPDATED_SUCCESS_LABEL,
  ERROR_LABEL,
  ERROR_MESSAGE_LABEL,
} from "../constants/Dictionary";
import {
  AddCollaboratorsProjects,
  UpdateCollaboratorsProjectsRequest,
} from "../types/api/projects.type";
import HistoryProjectRolesService from "../services/api/projects/ProjectRoles.service";
import HistoryProjectSectorService from "../services/api/projects/ProjectSector.service";
import CollaboratorsHistoryProfessionalExperiencesService from "../services/api/collaborators/CollaboratorsHistoryProfessionalExperiences.service";
import { useEffect, useState } from "react";
import { useDebounce } from "use-debounce";

export const useHistoryProjectFilters = (profileId: number) => {
  const { notification } = useNotification();

  const { data, isLoading, isError } = useQuery({
    queryKey: [`get-filters-history-projects-${profileId}`],
    queryFn: () => FiltersService.listForCollaboratorHistoryProjects(profileId),
    staleTime: RefreshReactQuery.MODERATE,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading };
};

interface useHistoryProjectsProps {
  profileId: number;
  pageNumber: number;
  searchDebouncedValue: string;
  filters: FilterProps[];
}

export const useHistoryProjects = ({
  profileId,
  pageNumber,
  searchDebouncedValue,
  filters,
}: useHistoryProjectsProps) => {
  const { notification } = useNotification();

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: [
      `get-collaborator-history-projects-${profileId}`,
      pageNumber,
      searchDebouncedValue,
      filters,
    ],
    queryFn: ({ signal }) =>
      CollaboratorsHistoryProjectsService.list(
        profileId,
        COLLABORATORS_PAGE_SIZE,
        pageNumber,
        [
          ...filters,
          ...(searchDebouncedValue
            ? [{ key: "search", value: [searchDebouncedValue] }]
            : []),
        ],
        signal,
      ),
    staleTime: RefreshReactQuery.MODERATE,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

export const useHistoryProjectDelete = (profileId: number) => {
  const queryClient = useQueryClient();
  const { notification } = useNotification();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (selectedProjectId: string) =>
      CollaboratorsHistoryProjectsService.delete(profileId, selectedProjectId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`get-collaborator-history-projects-${profileId}`],
      });
      notification.success(DATA_DELETED_SUCCESS_LABEL, undefined, "toast");
    },
    onError: (error) => {
      console.error(`Error deleting technical skills: ${error}`);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync, isPending };
};

export const useHistoryProjectAdd = (profileId: number) => {
  const queryClient = useQueryClient();
  const { notification } = useNotification();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (addBody: AddCollaboratorsProjects) =>
      CollaboratorsHistoryProjectsService.add(profileId, addBody),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`get-collaborator-history-projects-${profileId}`],
      });
      notification.success(DATA_ADDED_SUCCESS_LABEL, undefined, "toast");
    },
    onError: (error) => {
      console.error(`Error adding technical skills: ${error}`);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync, isPending };
};

export const useHistoryProjectUpdate = (profileId: number) => {
  const queryClient = useQueryClient();
  const { notification } = useNotification();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      body,
      projectId,
    }: {
      body: UpdateCollaboratorsProjectsRequest;
      projectId: number;
    }) =>
      CollaboratorsHistoryProjectsService.update(profileId, projectId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`get-collaborator-history-projects-${profileId}`],
      });
      notification.success(DATA_UPDATED_SUCCESS_LABEL, undefined, "toast");
    },
    onError: (error) => {
      console.error(`Error updating technical skills: ${error}`);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync, isPending };
};

export const useRoleOptionsData = () => {
  const { notification } = useNotification();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 500);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isError,
  } = useInfiniteQuery({
    queryKey: ["get-infinite-roles", debouncedSearch],
    queryFn: ({ pageParam, signal }) =>
      HistoryProjectRolesService.list(
        HISTORY_PROJECT_ROLES_PAGE_SIZE,
        pageParam as number,
        debouncedSearch ? [{ key: "search", value: [debouncedSearch] }] : [],
        signal,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const { pageNumber, pageSize, total } = lastPage.pagination;
      return (pageNumber + 1) * pageSize < total ? pageNumber + 1 : undefined;
    },
    staleTime: RefreshReactQuery.MODERATE,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return {
    data: data?.pages.flatMap((page) => page.data) ?? [],
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    setSearch,
    clearSearch: () => setSearch(""),
  };
};

export const useSectorOptionsData = () => {
  const { notification } = useNotification();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 500);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isError,
  } = useInfiniteQuery({
    queryKey: ["get-infinite-sectors", debouncedSearch],
    queryFn: ({ pageParam, signal }) =>
      HistoryProjectSectorService.list(
        HISTORY_PROJECT_SECTORS_PAGE_SIZE,
        pageParam as number,
        debouncedSearch ? [{ key: "search", value: [debouncedSearch] }] : [],
        signal,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const { pageNumber, pageSize, total } = lastPage.pagination;
      return (pageNumber + 1) * pageSize < total ? pageNumber + 1 : undefined;
    },
    staleTime: RefreshReactQuery.MODERATE,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return {
    data: data?.pages.flatMap((page) => page.data) ?? [],
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    setSearch,
    clearSearch: () => setSearch(""),
  };
};

export const useCompanyOptionsData = (profileId: number) => {
  const { notification } = useNotification();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 500);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isError,
  } = useInfiniteQuery({
    queryKey: ["get-infinite-company", profileId, debouncedSearch],
    queryFn: ({ pageParam, signal }) =>
      CollaboratorsHistoryProfessionalExperiencesService.list(
        profileId,
        HISTORY_PROJECT_COMPANY_PAGE_SIZE,
        pageParam as number,
        debouncedSearch ? [{ key: "search", value: [debouncedSearch] }] : [],
        signal,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const { pageNumber, pageSize, total } = lastPage.pagination;
      return (pageNumber + 1) * pageSize < total ? pageNumber + 1 : undefined;
    },
    staleTime: RefreshReactQuery.MODERATE,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return {
    data: data?.pages.flatMap((page) => page.data) ?? [],
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    setSearch,
    search,
  };
};
